/**
 * Where the driver map opens (Warren, item 11, 2026-10-23: "The map must
 * open on the driver's location if allowed, otherwise their region or North
 * America, never an empty or polar view").
 *
 * What went wrong before: every driver opened on one fixed globe pose,
 * [-98.5, 40] at zoom 2.2. At that zoom one ordinary upward flick (the
 * gesture that scrolls the terms card just before) turns the globe by about
 * fifty degrees of latitude, and the globe lets the camera rest over the
 * South Pole, so a new driver could be one or two swipes from Antarctica.
 * No coordinate pair in the start path was swapped; every hand-off to
 * mapbox-gl was already [lng, lat]. The guards below still reject a swapped
 * pair (Toronto read as [43.65, -79.38] lands at 79 S) with a warning.
 *
 * Order, first that applies:
 *   1. the driver's position, when the browser lets us read it (centred at
 *      LOCATION_ZOOM, and recentred when the fix arrives after first paint);
 *   2. their region, from the phone's time zone (no new tracking: the zone
 *      is already on the device and nothing here leaves it);
 *   3. their country, from the phone's language region (en-CA, es-MX);
 *   4. North America, the pose the map always opened on.
 *
 * Every coordinate is checked before use: finite, latitude -90..90,
 * longitude -180..180, and for a camera centre also inside the inhabited
 * band (START_MIN_LAT..START_MAX_LAT) and not 0,0. A bad one is dropped
 * with a console warning. Warnings about the driver's own position never
 * carry the values: console output can reach error reports as breadcrumbs.
 *
 * Pure apart from the device reads in deviceTimeZone / deviceLocale, so the
 * rules are testable without a browser.
 */

/** Mapbox order: longitude first. */
export type LngLat = [lng: number, lat: number];

export interface MapCamera {
  center: LngLat;
  zoom: number;
}

export type StartSource = 'location' | 'region' | 'country' | 'north-america';

export interface StartCamera extends MapCamera {
  source: StartSource;
  /** Plain name for logs and tests. Never shown to the driver. */
  label: string;
}

/** Inhabited latitudes, Cape Horn (about 56 S) to the north coast of
 *  Alaska (about 71 N). A centre outside is a polar view. */
export const START_MIN_LAT = -56;
export const START_MAX_LAT = 72;

/** Zoom the map opens at on the driver's own position: a few kilometres
 *  around them, so the nearest stations are on screen. */
export const LOCATION_ZOOM = 11;

/** The pose the map has always opened on (tuned against the admin
 *  dashboard's OVERVIEW_CAMERA): the whole continent on a phone. */
export const NORTH_AMERICA: StartCamera = {
  center: [-98.5, 40],
  zoom: 2.2,
  source: 'north-america',
  label: 'North America',
};

export type Warn = (message: string, detail?: unknown) => void;

const consoleWarn: Warn = (message, detail) => {
  if (detail === undefined) console.warn(`[map] ${message}`);
  else console.warn(`[map] ${message}`, detail);
};

function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

/** A real WGS-84 point: finite, latitude -90..90, longitude -180..180. */
export function isLngLatInRange(lng: unknown, lat: unknown): boolean {
  return isFiniteNumber(lng) && isFiniteNumber(lat)
    && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

/** A point the map may open on: in range, inside the inhabited band, and
 *  not the 0,0 "null island" a missing value turns into. */
export function isPlausibleCenter(lng: unknown, lat: unknown): boolean {
  if (!isLngLatInRange(lng, lat)) return false;
  const la = lat as number;
  const lo = lng as number;
  if (la < START_MIN_LAT || la > START_MAX_LAT) return false;
  if (Math.abs(la) < 0.5 && Math.abs(lo) < 0.5) return false;
  return true;
}

/** Latitude pulled into the inhabited band. */
export function clampToStartBand(lat: number): number {
  return Math.min(START_MAX_LAT, Math.max(START_MIN_LAT, lat));
}

/** Below this zoom the map is a whole globe on a phone: the view reaches
 *  about sixty degrees either side of its centre. */
export const GLOBE_VIEW_ZOOM = 4;
/** Centre band for those globe views, so a pole never sits mid-screen. */
export const GLOBE_MIN_LAT = -30;
export const GLOBE_MAX_LAT = 58;

/**
 * The polar guard: the latitude a settled camera is pulled back to. Close
 * in, the inhabited band; on a whole-globe view a tighter band, because
 * there a centre at 56 S still puts the South Pole in the middle of the
 * lower half of the screen.
 */
export function clampCameraLat(lat: number, zoom: number): number {
  if (zoom < GLOBE_VIEW_ZOOM) return Math.min(GLOBE_MAX_LAT, Math.max(GLOBE_MIN_LAT, lat));
  return clampToStartBand(lat);
}

/** Longitude folded into -180..180 (a panned globe can report 190). */
export function wrapLng(lng: number): number {
  if (lng >= -180 && lng <= 180) return lng;
  return ((((lng + 180) % 360) + 360) % 360) - 180;
}

/**
 * `value` as a usable map centre, or null with a warning saying why.
 * `personal` marks the driver's own position: the warning then names the
 * problem without the numbers.
 */
export function validCenter(
  value: unknown,
  source: string,
  opts: { personal?: boolean; warn?: Warn } = {},
): LngLat | null {
  const warn = opts.warn ?? consoleWarn;
  const detail = opts.personal ? undefined : value;
  if (!Array.isArray(value) || value.length !== 2) {
    warn(`Ignoring ${source} center: not a [lng, lat] pair.`, detail);
    return null;
  }
  const [lng, lat] = value as [unknown, unknown];
  if (!isFiniteNumber(lng) || !isFiniteNumber(lat)) {
    warn(`Ignoring ${source} center: not finite numbers.`, detail);
    return null;
  }
  if (isPlausibleCenter(lng, lat)) return [lng, lat];
  if (isPlausibleCenter(lat, lng)) {
    warn(`Ignoring ${source} center: it reads as [lat, lng], Mapbox wants [lng, lat].`, detail);
    return null;
  }
  if (!isLngLatInRange(lng, lat)) {
    warn(`Ignoring ${source} center: outside latitude -90..90 or longitude -180..180.`, detail);
    return null;
  }
  warn(`Ignoring ${source} center: a polar or 0,0 view.`, detail);
  return null;
}

/** A saved or reported camera, checked: a valid centre and a finite zoom. */
export function validCamera(
  value: unknown,
  source: string,
  opts: { warn?: Warn } = {},
): MapCamera | null {
  const warn = opts.warn ?? consoleWarn;
  if (!value || typeof value !== 'object') {
    warn(`Ignoring ${source} camera: missing.`);
    return null;
  }
  const v = value as { center?: unknown; zoom?: unknown };
  const center = validCenter(v.center, source, { warn });
  if (!center) return null;
  if (!isFiniteNumber(v.zoom) || v.zoom < 0 || v.zoom > 24) {
    warn(`Ignoring ${source} camera: zoom is not a usable number.`, v.zoom);
    return null;
  }
  return { center, zoom: v.zoom };
}

/** The Geolocation API's coords as a centre (named fields, so no swap is
 *  possible here), checked like any other. Never logs the values. */
export function positionCenter(
  coords: { latitude?: unknown; longitude?: unknown } | null | undefined,
  opts: { warn?: Warn } = {},
): LngLat | null {
  if (!coords) {
    (opts.warn ?? consoleWarn)('Ignoring geolocation: no coordinates.');
    return null;
  }
  return validCenter([coords.longitude, coords.latitude], 'geolocation', { personal: true, warn: opts.warn });
}

// ── Region from the phone's time zone ──────────────────────────────────────

export interface Region {
  center: LngLat;
  zoom: number;
  label: string;
}

const SOUTHERN_ONTARIO: Region = { center: [-79.4, 43.9], zoom: 6, label: 'Southern Ontario' };
const SOUTHERN_QUEBEC: Region = { center: [-73.6, 45.6], zoom: 6, label: 'Southern Quebec' };
const LOWER_MAINLAND: Region = { center: [-122.8, 49.3], zoom: 6, label: 'British Columbia' };
const ALBERTA: Region = { center: [-113.8, 52.3], zoom: 5.5, label: 'Alberta' };
const SASKATCHEWAN: Region = { center: [-105.5, 51.2], zoom: 5.5, label: 'Saskatchewan' };
const MANITOBA: Region = { center: [-97.1, 49.9], zoom: 6, label: 'Manitoba' };
const MARITIMES: Region = { center: [-63.6, 45.0], zoom: 5.5, label: 'Nova Scotia' };
const NEW_BRUNSWICK: Region = { center: [-65.8, 46.2], zoom: 6, label: 'New Brunswick' };
const NEWFOUNDLAND: Region = { center: [-53.5, 47.6], zoom: 6, label: 'Newfoundland' };
const YUKON: Region = { center: [-135.05, 60.72], zoom: 6, label: 'Yukon' };
const US_EAST: Region = { center: [-77.5, 39.5], zoom: 4.4, label: 'Eastern United States' };
const MICHIGAN: Region = { center: [-83.5, 42.6], zoom: 6, label: 'Michigan' };
const INDIANA: Region = { center: [-86.15, 39.8], zoom: 6, label: 'Indiana' };
const KENTUCKY: Region = { center: [-85.75, 38.25], zoom: 6, label: 'Kentucky' };
const US_CENTRAL: Region = { center: [-92, 37], zoom: 4.2, label: 'Central United States' };
const US_MOUNTAIN: Region = { center: [-108.5, 40], zoom: 4.8, label: 'Mountain West' };
const IDAHO: Region = { center: [-116.2, 43.6], zoom: 6, label: 'Idaho' };
const ARIZONA: Region = { center: [-111.9, 33.5], zoom: 6, label: 'Arizona' };
const US_WEST: Region = { center: [-120.5, 40.5], zoom: 4.6, label: 'West Coast' };
const ALASKA: Region = { center: [-149.9, 61.2], zoom: 6, label: 'Alaska' };
const HAWAII: Region = { center: [-157.9, 21.4], zoom: 7, label: 'Hawaii' };
const PUERTO_RICO: Region = { center: [-66.4, 18.2], zoom: 8, label: 'Puerto Rico' };
const CENTRAL_MEXICO: Region = { center: [-99.13, 19.43], zoom: 6, label: 'Central Mexico' };
const NORTHEAST_MEXICO: Region = { center: [-100.3, 25.67], zoom: 6, label: 'Northeast Mexico' };
const BAJA: Region = { center: [-117.0, 32.5], zoom: 7, label: 'Baja California' };
const YUCATAN: Region = { center: [-86.85, 21.16], zoom: 7, label: 'Quintana Roo' };

/** IANA zone (and the legacy aliases phones still report) to region. */
export const ZONE_REGIONS: Readonly<Record<string, Region>> = {
  'America/Toronto': SOUTHERN_ONTARIO,
  'America/Nipigon': SOUTHERN_ONTARIO,
  'America/Thunder_Bay': SOUTHERN_ONTARIO,
  'Canada/Eastern': SOUTHERN_ONTARIO,
  'America/Montreal': SOUTHERN_QUEBEC,
  'America/Vancouver': LOWER_MAINLAND,
  'Canada/Pacific': LOWER_MAINLAND,
  'America/Edmonton': ALBERTA,
  'America/Yellowknife': ALBERTA,
  'Canada/Mountain': ALBERTA,
  'America/Regina': SASKATCHEWAN,
  'America/Swift_Current': SASKATCHEWAN,
  'Canada/Saskatchewan': SASKATCHEWAN,
  'America/Winnipeg': MANITOBA,
  'Canada/Central': MANITOBA,
  'America/Halifax': MARITIMES,
  'America/Glace_Bay': MARITIMES,
  'Canada/Atlantic': MARITIMES,
  'America/Moncton': NEW_BRUNSWICK,
  'America/St_Johns': NEWFOUNDLAND,
  'Canada/Newfoundland': NEWFOUNDLAND,
  'America/Whitehorse': YUKON,
  'Canada/Yukon': YUKON,
  'America/New_York': US_EAST,
  'US/Eastern': US_EAST,
  'America/Detroit': MICHIGAN,
  'US/Michigan': MICHIGAN,
  'America/Indiana/Indianapolis': INDIANA,
  'America/Indianapolis': INDIANA,
  'US/East-Indiana': INDIANA,
  'America/Kentucky/Louisville': KENTUCKY,
  'America/Louisville': KENTUCKY,
  'America/Chicago': US_CENTRAL,
  'US/Central': US_CENTRAL,
  'America/Denver': US_MOUNTAIN,
  'US/Mountain': US_MOUNTAIN,
  'America/Boise': IDAHO,
  'America/Phoenix': ARIZONA,
  'US/Arizona': ARIZONA,
  'America/Los_Angeles': US_WEST,
  'US/Pacific': US_WEST,
  'America/Anchorage': ALASKA,
  'US/Alaska': ALASKA,
  'Pacific/Honolulu': HAWAII,
  'US/Hawaii': HAWAII,
  'America/Puerto_Rico': PUERTO_RICO,
  'America/Mexico_City': CENTRAL_MEXICO,
  'Mexico/General': CENTRAL_MEXICO,
  'America/Monterrey': NORTHEAST_MEXICO,
  'America/Tijuana': BAJA,
  'Mexico/BajaNorte': BAJA,
  'America/Cancun': YUCATAN,
};

/** Country (from the language tag's region, en-CA) to a country view. */
export const COUNTRY_REGIONS: Readonly<Record<string, Region>> = {
  CA: { center: [-90, 50], zoom: 3, label: 'Canada' },
  US: { center: [-98.5, 39], zoom: 3, label: 'United States' },
  MX: { center: [-102, 23.5], zoom: 4.2, label: 'Mexico' },
};

/** The region subtag of a BCP 47 tag ('fr-CA' to 'CA'), or null. */
export function localeCountry(locale: string | null | undefined): string | null {
  if (!locale) return null;
  const parts = locale.replace(/_/g, '-').split('-');
  for (const p of parts.slice(1)) {
    if (/^[A-Za-z]{2}$/.test(p)) return p.toUpperCase();
  }
  return null;
}

/**
 * The opening camera before (or without) the driver's position: their
 * region from the zone, else their country from the language tag, else
 * North America. A French-language phone on Eastern time opens on Quebec
 * (Ontario and Quebec share America/Toronto).
 */
export function regionStartCamera(timeZone: string | null | undefined, locale: string | null | undefined): StartCamera {
  const zone = timeZone?.trim() ?? '';
  let region = zone ? ZONE_REGIONS[zone] : undefined;
  if (region === SOUTHERN_ONTARIO && /^fr\b/i.test(locale ?? '')) region = SOUTHERN_QUEBEC;
  if (region) return { center: region.center, zoom: region.zoom, source: 'region', label: region.label };
  const country = localeCountry(locale);
  const byCountry = country ? COUNTRY_REGIONS[country] : undefined;
  if (byCountry) return { center: byCountry.center, zoom: byCountry.zoom, source: 'country', label: byCountry.label };
  return NORTH_AMERICA;
}

/** The phone's IANA zone, or null. */
export function deviceTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/** The phone's first language tag ('en-CA'), or null. */
export function deviceLocale(): string | null {
  if (typeof navigator === 'undefined') return null;
  return navigator.languages?.[0] ?? navigator.language ?? null;
}

// ── The driver's position ──────────────────────────────────────────────────

export type GeoPermission = 'granted' | 'denied' | 'prompt' | 'unknown';
/** What the driver answered last time on this device; never a position. */
export type GeoMemory = 'allowed' | 'declined' | null;

export const GEO_MEMORY_KEY = 'evi.map.geolocation';

/**
 * Whether opening the map asks for the position. Granted: always (no
 * prompt). Denied: never. Prompt or unknown (Safari before 16, or a browser
 * that will not say): the first time, and after that only while the driver
 * keeps allowing it, so a driver who said no is not asked on every open. The
 * locate button still asks whenever it is tapped.
 */
export function shouldRequestPosition(permission: GeoPermission, memory: GeoMemory): boolean {
  if (permission === 'granted') return true;
  if (permission === 'denied') return false;
  return memory !== 'declined';
}

export type GeoOutcome = 'located' | 'denied' | 'unavailable' | 'timeout' | 'unsupported' | 'invalid';

/** Memory after an answer: a fix means allowed, a refusal means declined,
 *  anything else (no fix, slow fix) leaves the answer where it was. */
export function memoryAfter(outcome: GeoOutcome, previous: GeoMemory): GeoMemory {
  if (outcome === 'located') return 'allowed';
  if (outcome === 'denied') return 'declined';
  return previous;
}

export function readGeoMemory(storage: Pick<Storage, 'getItem'> | null | undefined): GeoMemory {
  try {
    const v = storage?.getItem(GEO_MEMORY_KEY);
    return v === 'allowed' || v === 'declined' ? v : null;
  } catch {
    return null;
  }
}

export function writeGeoMemory(storage: Pick<Storage, 'setItem'> | null | undefined, memory: GeoMemory): void {
  if (!memory) return;
  try {
    storage?.setItem(GEO_MEMORY_KEY, memory);
  } catch {
    // Private mode or quota: the driver may be asked again next time.
  }
}

/** The browser's geolocation permission state, 'unknown' when it will not
 *  say (no Permissions API, or it throws for 'geolocation'). */
export async function readGeoPermission(
  permissions: Pick<Permissions, 'query'> | null | undefined,
): Promise<GeoPermission> {
  if (!permissions?.query) return 'unknown';
  try {
    const status = await permissions.query({ name: 'geolocation' as PermissionName });
    const s = status.state;
    return s === 'granted' || s === 'denied' || s === 'prompt' ? s : 'unknown';
  } catch {
    return 'unknown';
  }
}

export interface StartPositionDeps {
  geolocation: Pick<Geolocation, 'getCurrentPosition'> | null | undefined;
  permissions: Pick<Permissions, 'query'> | null | undefined;
  storage: Pick<Storage, 'getItem' | 'setItem'> | null | undefined;
  warn?: Warn;
  /** How long the browser may take for a fix once allowed. A late fix still
   *  recentres the map, so this is generous. */
  timeoutMs?: number;
}

export interface StartPositionResult {
  outcome: GeoOutcome | 'skipped';
  center: LngLat | null;
}

/**
 * Ask the browser for the driver's position for the opening camera, per the
 * policy above. Resolves when the browser answers (which may be long after
 * first paint). The position is only returned to the caller, never stored
 * or sent anywhere.
 */
export async function requestStartPosition(deps: StartPositionDeps): Promise<StartPositionResult> {
  const warn = deps.warn ?? consoleWarn;
  const geolocation = deps.geolocation;
  if (!geolocation?.getCurrentPosition) return { outcome: 'unsupported', center: null };
  const permission = await readGeoPermission(deps.permissions);
  const memory = readGeoMemory(deps.storage);
  if (!shouldRequestPosition(permission, memory)) return { outcome: 'skipped', center: null };

  const outcome = await new Promise<{ outcome: GeoOutcome; center: LngLat | null }>((resolve) => {
    try {
      geolocation.getCurrentPosition(
        (pos) => {
          const center = positionCenter(pos?.coords, { warn });
          resolve(center ? { outcome: 'located', center } : { outcome: 'invalid', center: null });
        },
        (err) => {
          // 1 PERMISSION_DENIED, 2 POSITION_UNAVAILABLE, 3 TIMEOUT.
          const code = err?.code;
          resolve({ outcome: code === 1 ? 'denied' : code === 3 ? 'timeout' : 'unavailable', center: null });
        },
        { enableHighAccuracy: false, timeout: deps.timeoutMs ?? 20_000, maximumAge: 5 * 60_000 },
      );
    } catch {
      resolve({ outcome: 'unavailable', center: null });
    }
  });
  writeGeoMemory(deps.storage, memoryAfter(outcome.outcome, memory));
  return outcome;
}
